import { createSelector } from 'reselect';

import { ICampaignItem } from '../../../types/campaigns';
import { CreateSendMethodEnum } from '../../../types/createSendFlow';
import {
  buildLocationSendItems,
  getLocationRowKey,
  isMskuItemUnset,
  TLocationContentItem,
} from '../../../utils/createSendLocation';
import { selectCsItems, selectCsKits } from './contents';
import { selectCreateSendMethod } from './method';

export interface IUnsetMskuTarget {
  boxId: string;
  itemId: string;
  name: string;
}

const toTarget = (item: ICampaignItem, boxId: string): IUnsetMskuTarget => ({
  boxId,
  itemId: getLocationRowKey(item),
  name: item.name ?? '',
});

export const selectCsUnsetMskuTargets = createSelector(
  selectCreateSendMethod,
  selectCsKits,
  (method, kits): IUnsetMskuTarget[] => {
    if (method !== CreateSendMethodEnum.Location) {
      return [];
    }

    return (kits ?? []).flatMap((kit) =>
      (kit.items ?? []).filter(isMskuItemUnset).map((item) => toTarget(item, kit.box_id)),
    );
  },
);

export const selectCsUnsetMskuCount = createSelector(selectCsUnsetMskuTargets, (targets) => targets.length);

export const selectCsFirstUnsetMsku = createSelector(
  selectCsUnsetMskuTargets,
  (targets): IUnsetMskuTarget | null => targets[0] ?? null,
);

export const selectCsUnsetMskuKey = createSelector(selectCsUnsetMskuTargets, (targets): string | null =>
  targets.length ? targets.map((target) => `${target.boxId}:${target.itemId}`).join('|') : null,
);

export const selectIsCsLocationSendEmpty = createSelector(
  selectCreateSendMethod,
  selectCsKits,
  selectCsItems,
  (method, kits, items): boolean =>
    method === CreateSendMethodEnum.Location &&
    buildLocationSendItems(kits, items as TLocationContentItem[]).length === 0,
);
